import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Button, DialogOverlay, DialogOverlayActions, Paragraph, Separator } from '@fs/zion-ui'
import { Trans, useTranslation } from 'react-i18next'

/**
 * Confirms moving a person into a new group of their own. Used by both the per-member control and
 * the self-removal action. Confirming applies the move immediately, not behind the card's SAVE.
 *
 * The dialog does not close itself on confirm — the card closes it once the move resolves, so MOVE
 * can report progress here rather than in the card footer.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.overlay Controls from `useOverlay()`.
 * @param {string} props.personName Full name of the person being moved out.
 * @param {Array<Object>} [props.breakingRelationships] Relationships the move will remove; each
 * item needs `id`, `personName` and `relationshipLabel`.
 * @param {Function} [props.onConfirm] Runs when the patron confirms.
 * @param {boolean} [props.moving=false] Whether the move is in flight.
 * @returns {JSX.Element}
 */
const MoveOutOfGroupDialogOverlay = ({
  overlay,
  personName,
  breakingRelationships = [],
  onConfirm = () => {},
  moving = false,
}) => {
  const [t] = useTranslation()

  const handleCancel = useCallback(() => {
    overlay.close()
  }, [overlay])

  return (
    <DialogOverlay
      {...overlay}
      title={t('move-out-of-group-dialog.title', 'Move out of group')}
      size="sm"
      footer={
        <DialogOverlayActions>
          <Button
            emphasis="low"
            onClick={handleCancel}
            disabled={moving}
            data-testid="move-out-of-group-dialog-cancel-button"
            color="gray"
          >
            {t('move-out-of-group-dialog.cancelBtn', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            onClick={onConfirm}
            disabled={moving}
            data-testid="move-out-of-group-dialog-move-button"
          >
            {moving
              ? t('move-out-of-group-dialog.movingBtn', 'Moving...')
              : t('move-out-of-group-dialog.moveBtn', 'Move')}
          </Button>
        </DialogOverlayActions>
      }
    >
      <Paragraph size="sm">
        <Trans i18nKey="move-out-of-group-dialog.message" values={{ personName }} components={{ bold: <b /> }}>
          <bold>{personName}</bold> will be moved into a new group of their own, and set as the primary person of that
          group.
        </Trans>
      </Paragraph>

      {breakingRelationships.length > 0 && (
        <>
          <Separator size="xs" />
          <Paragraph size="sm" balanced>
            {t('move-out-of-group-dialog.relationships-removed', 'These relationships will be removed:')}
          </Paragraph>
          <ul data-testid="move-out-of-group-breaking-relationships">
            {breakingRelationships.map((rel) => (
              <li key={rel.id}>{`${rel.personName} - ${rel.relationshipLabel}`}</li>
            ))}
          </ul>
          <Separator size="xs" />
          <Paragraph size="xs" secondary>
            {t('move-out-of-group-dialog.other-groups-unaffected', 'Relationships in other groups are not affected.')}
          </Paragraph>
        </>
      )}
    </DialogOverlay>
  )
}

MoveOutOfGroupDialogOverlay.propTypes = {
  overlay: PropTypes.shape({
    open: PropTypes.func,
    close: PropTypes.func,
    isOpen: PropTypes.bool,
  }).isRequired,
  personName: PropTypes.string,
  breakingRelationships: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      personName: PropTypes.string,
      relationshipLabel: PropTypes.string,
    })
  ),
  onConfirm: PropTypes.func,
  moving: PropTypes.bool,
}

MoveOutOfGroupDialogOverlay.defaultProps = {
  personName: '',
  breakingRelationships: [],
  onConfirm: () => {},
  moving: false,
}

export default MoveOutOfGroupDialogOverlay
